<template>
  <a-layout class="user-cash-balance-report">
    <TitleBreadcrumb />
    <div class="content-base container">
      <a-card class="user-cash-balance-report-card">
        <a-row
          type="flex"
          justify="space-between"
          align="bottom"
          :class="{ 'flex-direction-reverse': isMobile }"
          :gutter="[16, 16]"
        >
          <a-col :xs="24" :sm="24" :md="12">
            <range-picker
              v-model="dateRange"
              :placeholder="[
                labels.table.dateRange.placeholder.startdate,
                labels.table.dateRange.placeholder.enddate,
              ]"
              :format="[dateFormat, dateFormat]"
              :formitem="{
                label: labels.table.dateRange.label,
              }"
              :allowClear="true"
            />
          </a-col>
          <a-col :xs="24" :sm="24" :md="6" style="text-align: right">
            <a-button
              type="primary"
              @click="onSearch"
              :loading="loading"
              :disabled="loading"
              style="margin-bottom: 24px"
              class="action-button-generate"
            >
              {{ labels.table.generate }}
            </a-button>
          </a-col>
        </a-row>
        <a-divider
          orientation="center"
          type="horizontal"
          :style="{ marginBottom: 0 }"
        />
        <a-row type="flex" justify="center" :gutter="[16, 16]">
          <a-col :xs="24">
            <standard-table
              class="table-base"
              ref="standardTable"
              :loading="loading"
              :data="dataSource"
              :columns="columns"
              :locale="locale"
              :handleShowTotal="false"
              :rowKey="'date'"
              :paginate="false"
            >
              <template v-if="dataSource.length" #footer>
                <div class="totals">
                  <span class="label">{{ labels.table.total }}</span>
                  <span class="value">{{ totalAmount | formatNumber }}</span>
                  <span class="value">{{ wonTotalAmount | formatNumber }}</span>
                </div>
              </template>
            </standard-table>
          </a-col>
        </a-row>
      </a-card>
    </div>
  </a-layout>
</template>
<script>
import { mapActions, mapState, mapGetters } from "vuex";
import accounting from "accounting";
import TitleBreadcrumb from "@/components/layout/client/TitleBreadcrumb.vue";
import loadingMixin from "@/mixins/loadingMixin";
import { pageTitle } from "@/utils/utils";
import labels from "@/utils/labels";
import StandardTable from "@/components/core/table/StandardTable.vue";
import RangePicker from "@/components/core/VeeValidateForm/RangePicker.vue";
import { DATE_FORMAT } from "../../utils/utils";
export default {
  components: { TitleBreadcrumb, StandardTable, RangePicker },
  name: "UserCashBalanceReport",
  mixins: [loadingMixin],
  metaInfo: {
    title: pageTitle(labels.orders.userCashBalanceReport.subtitle),
    meta: [
      {
        name: "title",
        content: labels.appName,
      },
      {
        name: "description",
        content: labels.orders.userCashBalanceReport.subtitle,
      },
    ],
  },
  filters: {
    formatNumber(value) {
      return accounting.formatNumber(value || 0, 2, ",", ".");
    },
  },
  beforeRouteEnter(_to, _from, next) {
    next((vm) => {
      if (!vm.loggedUser.ecb) {
        vm.$router.push({ name: "home", replace: true });
      }
      vm.cleanData();
    });
  },
  data() {
    return {
      labels: labels.orders.userCashBalanceReport,
      search: "",
      activeRow: null,
      dataSource: [],
      loading: false,
      dateRange: [],
      totalAmount: 0,
      wonTotalAmount: 0,
      generated: false,
    };
  },
  created() {
    if (!this.loggedUser.ecb) {
      this.$router.push({ name: "home", replace: true });
    }
    this.cleanData();
  },
  methods: {
    ...mapActions("orders", ["fetchUserCashBalanceReport"]),
    cleanData() {
      this.dataSource = [];
      this.dateRange = [];
      this.totalAmount = 0;
      this.wonTotalAmount = 0;
    },
    async onSearch() {
      try {
        if (!this.dateRange || !this.dateRange.length) {
          this.$notification.error({
            message: this.labels.error,
            description: this.labels.table.dateRange.required,
          });
          return;
        }

        this.loading = true;
        const { data } = await this.fetchUserCashBalanceReport({
          dateRange: this.dateRange.map((date) =>
            this.$moment(date).format(DATE_FORMAT.SERVER_DATE)
          ),
        });
        this.dataSource = data.data;
        this.totalAmount = data.totalAmount;
        this.wonTotalAmount = data.wonTotalAmount;

        if (this.dataSource.length) {
          this.generated = true;
        } else {
          this.$notification.info({
            message: this.labels.table.info,
            description: this.labels.table.emptyTextNoData,
          });
        }
      } catch (error) {
        this.$notification.error({
          message: this.labels.error,
          description: error.message,
        });
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters("clientAuth", ["getLoggedUser"]),
    loggedUser() {
      return this.getLoggedUser;
    },
    dateFormat() {
      return "DD/MM/YYYY";
    },
    locale() {
      return {
        emptyText: this.generated
          ? this.labels.table.emptyTextNoData
          : this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.date,
          dataIndex: "date",
          key: "date",
          customRender: (text) => {
            return this.$moment(text).format(DATE_FORMAT.MOMENT_DATE);
          },
        },
        {
          title: this.labels.table.columns.totalAmount,
          dataIndex: "total_amount",
          key: "total_amount",
          width: "20%",
          customRender: (text) => {
            return accounting.formatNumber(text || 0, 2, ",", ".");
          },
        },
        {
          title: this.labels.table.columns.wonTotalAmount,
          dataIndex: "won_total_amount",
          key: "won_total_amount",
          width: "20%",
          customRender: (text) => {
            return accounting.formatNumber(text || 0, 2, ",", ".");
          },
        },
      ];
    },
  },
  beforeUnmount() {
    this.cleanData();
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.user-cash-balance-report {
  max-width: 100% !important;
  font-family: "Roboto", sans-serif;
  background-color: transparent;
  @media screen and (max-width: $screen-mobile) {
    max-width: 100% !important;
  }
  .content-base {
    padding-top: 60px;
    @media screen and (max-width: $screen-mobile) {
      max-width: 100% !important;
    }
  }
  &-card {
    background-color: var(--card-shop-bg);
    border-color: var(--card-shop-shadow);
    padding: 1.125rem;
    border-radius: 10px;
    @media screen and (max-width: $screen-mobile) {
      padding: 2.125rem 1rem;
      width: 100%;
    }
    .ant-input {
      border: 1px solid var(--text-active) !important;
      color: var(--text) !important;
      font-family: "Exo 2", sans-serif !important;
      background-color: transparent;
    }
    .ant-input-search-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--text-active) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-input-clear-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--danger) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-table {
      &:hover {
        transition: 0.5s ease;
        background: unset;
      }
      tr.ant-table-row:hover {
        transition: 0.5s ease;
        background: var(--table-hover) !important;
      }
      tr:nth-child(2n) {
        transition: 0.5s ease;
        background: var(--table-striped) !important;
      }
      tr.table-row-disabled,
      tr.table-row-disabled:hover {
        color: var(--table-disabled) !important;
        background: var(--table-disabled) !important;
        font-weight: 600;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        transition: 0.5s ease;
        background: none !important;
      }
      &-tbody,
      &-thead {
        color: var(--text) !important;
        tr {
          th {
            color: var(--text) !important;
            background-color: transparent !important;
          }
        }
      }
      &-footer {
        background: transparent;
      }
      .action-button-view {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
        transition: all 0.5s ease;
        &:hover {
          color: var(--btn-text-secondary);
          border-color: var(--btn-bc-secondary);
          background-color: var(--btn-bg-secondary);
          transition: all 0.5s ease;
        }
      }
      .ant-table-placeholder {
        background: var(--card-bg);
        color: var(--text);
        border-radius: 0;
      }
      @media screen and (max-width: $screen-mobile) {
        &-small {
          border: none;
        }
        &-body {
          margin: 0 1px;
        }
      }
    }
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
    }
    .ant-table-pagination.mini {
      .ant-pagination-prev .ant-pagination-item-link,
      .ant-pagination-next .ant-pagination-item-link {
        background-color: transparent !important;
        border-color: none !important;
        color: var(--text) !important;
      }
    }
    .ant-pagination-item {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
      a {
        color: var(--btn-text-secondary) !important;
      }
      &-active {
        color: var(--btn-text-primary) !important;
        background-color: var(--btn-bg-primary) !important;
        border-color: var(--btn-bc-primary) !important;
        a {
          color: var(--btn-text-primary) !important;
        }
      }
    }
    .ant-pagination {
      color: var(--text) !important;
    }
    .flex-direction-reverse {
      flex-direction: column-reverse;
    }
  }
  .text-danger {
    color: var(--danger) !important;
  }
  .ant-form-item-label label {
    color: var(--text) !important;
  }
  .totals {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    font-size: 1.125rem;
    .label {
      font-weight: 500;
      color: var(--text);
      width: 60%;
      text-transform: uppercase;
    }
    .value {
      font-weight: 500;
      color: var(--text);
      width: 20%;
      overflow-wrap: break-word;
      padding-left: 17px;
    }
  }
  .action-button-generate {
    color: var(--btn-text-primary);
    border-color: var(--btn-bc-primary);
    background-color: var(--btn-bg-primary);
    transition: all 0.5s ease;
    &:hover {
      color: var(--btn-text-primary);
      border-color: var(--btn-bc-primary);
      background-color: var(--btn-bg-primary);
      transition: all 0.5s ease;
    }
  }
}
</style>
